import React, { createContext, useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";

import Login from "./Login";
import TeamDashboard from "./TeamDashboard";
import PlayerStats from "./PlayerStats";
import AdminPage from "./AdminPage/AdminPage";
import TeamSelector from "./TeamSelector";

import { TeamProvider, useTeam } from "./TeamContext";

export const AuthContext = createContext();

function AppRoutes({ currentUser, logout }) {
  const { selectedTeam } = useTeam();

  return (
    <Routes>
      {/* LOGIN */}
      <Route path="/login" element={<Login />} />

      {/* TEAM SELECTION */}
      <Route
        path="/select-team"
        element={
          currentUser ? <TeamSelector /> : <Navigate to="/login" />
        }
      />

      {/* ADMIN */}
      <Route
        path="/admin"
        element={
          currentUser?.role === "admin" ? (
            <AdminPage onLogout={logout} />
          ) : (
            <Navigate to="/login" />
          )
        }
      />

      {/* TEAM DASHBOARD */}
      <Route
        path="/teams/:teamId"
        element={
          currentUser && selectedTeam ? (
            <TeamDashboard />
          ) : (
            <Navigate to="/select-team" />
          )
        }
      />

      {/* PLAYER STATS */}
      <Route
        path="/players"
        element={
          currentUser && selectedTeam ? (
            <PlayerStats />
          ) : (
            <Navigate to="/select-team" />
          )
        }
      />

      {/* FALLBACK */}
      <Route
        path="*"
        element={
          !currentUser ? (
            <Navigate to="/login" />
          ) : currentUser.role === "admin" ? (
            <Navigate to="/admin" />
          ) : (
            <Navigate to="/select-team" />
          )
        }
      />
    </Routes>
  );
}

export function App() {
  const [accessToken, setAccessToken] = useState(
    localStorage.getItem("accessToken")
  );
  const [currentUser, setCurrentUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);

  const logout = () => {
    localStorage.removeItem("accessToken");
    setAccessToken(null);
    setCurrentUser(null);
  };

  useEffect(() => {
    setLoadingUser(true);

    if (!accessToken) {
      setCurrentUser(null);
      setLoadingUser(false);
      return;
    }

    try {
      const payload = JSON.parse(atob(accessToken.split(".")[1]));

      if (payload.exp * 1000 < Date.now()) {
        console.warn("JWT expired, logging out");
        logout();
      } else {
        setCurrentUser({
          username: payload.sub,
          role: payload.role || "user",
        });
      }
    } catch (err) {
      console.error("Invalid JWT, logging out", err);
      logout();
    }

    setLoadingUser(false);
  }, [accessToken]);

  const login = (token) => {
    localStorage.setItem("accessToken", token);
    setAccessToken(token);
  };

  if (loadingUser) {
    return <p className="text-center mt-10">Loading user…</p>;
  }

  return (
    <AuthContext.Provider value={{ accessToken, currentUser, login, logout }}>
      <TeamProvider>
        <Router>
          <AppRoutes currentUser={currentUser} logout={logout} />
        </Router>
      </TeamProvider>
    </AuthContext.Provider>
  );
}

export default App;
